import { api } from './api';
import type {
  Generation,
  GenerationInput,
  ApiResponse,
  PaginatedResponse,
} from '@soundbyte/types';

/**
 * Generation Service
 *
 * Handles all generation-related operations.
 * Uses real API endpoints with session-based authentication.
 */
class GenerationService {
  /**
   * Create a new generation
   */
  async create(input: GenerationInput): Promise<ApiResponse<Generation>> {
    // If there's a voice file, upload it first
    if (input.voiceFile) {
      const uploadResponse = await api.upload<{ url: string; filename: string }>(
        '/uploads/voice',
        input.voiceFile,
        'file',
        { purpose: 'generation' }
      );

      if (!uploadResponse.success || !uploadResponse.data) {
        return {
          data: null,
          error: uploadResponse.error || { code: 'UPLOAD_FAILED', message: 'Failed to upload voice file' },
          success: false,
        };
      }

      // Create generation with uploaded voice URL
      return api.post<Generation>('/generations', {
        textPrompt: input.textPrompt,
        artStyle: input.artStyle,
        city: input.city,
        resolution: input.resolution,
        colorConfig: input.colorConfig,
        voiceUrl: uploadResponse.data.url,
      });
    }

    // Create generation without voice file
    return api.post<Generation>('/generations', {
      textPrompt: input.textPrompt,
      artStyle: input.artStyle,
      city: input.city,
      resolution: input.resolution,
      colorConfig: input.colorConfig,
    });
  }

  /**
   * Get user's generations with pagination
   */
  async getAll(
    page: number = 1,
    pageSize: number = 12
  ): Promise<ApiResponse<PaginatedResponse<Generation>>> {
    return api.get<PaginatedResponse<Generation>>('/generations', { page, pageSize });
  }

  /**
   * Get single generation by ID
   */
  async getById(id: string): Promise<ApiResponse<Generation>> {
    return api.get<Generation>(`/generations/${id}`);
  }

  /**
   * Toggle favorite status
   */
  async toggleFavorite(id: string): Promise<ApiResponse<Generation>> {
    return api.patch<Generation>(`/generations/${id}/favorite`);
  }

  /**
   * Toggle public visibility
   */
  async togglePublic(id: string): Promise<ApiResponse<Generation>> {
    return api.patch<Generation>(`/generations/${id}/visibility`);
  }

  /**
   * Delete a generation
   */
  async delete(id: string): Promise<ApiResponse<{ message: string }>> {
    return api.delete<{ message: string }>(`/generations/${id}`);
  }

  /**
   * Get favorite generations
   */
  async getFavorites(): Promise<ApiResponse<Generation[]>> {
    return api.get<Generation[]>('/generations/favorites');
  }

  /**
   * Download generation artwork
   */
  async download(id: string): Promise<ApiResponse<{ url: string }>> {
    return api.get<{ url: string }>(`/generations/${id}/download`);
  }

  /**
   * Get public gallery generations
   */
  async getPublicGallery(
    page: number = 1,
    pageSize: number = 20
  ): Promise<ApiResponse<PaginatedResponse<Generation>>> {
    return api.get<PaginatedResponse<Generation>>('/generations/public', { page, pageSize });
  }

  /**
   * Get playground status (maintenance mode)
   */
  async getPlaygroundStatus(): Promise<ApiResponse<{
    maintenanceMode: boolean;
    maintenanceMessage: string | null;
  }>> {
    return api.get<{
      maintenanceMode: boolean;
      maintenanceMessage: string | null;
    }>('/generations/status');
  }

  /**
   * Extract all story elements using AI (comprehensive extraction)
   */
  async extractStoryElements(story: string): Promise<ApiResponse<{
    success: boolean;
    elements?: {
      name?: string;
      places: string[];
      people: string[];
      emotions: string[];
      themes: string[];
      interests: string[];
      keywords: string[];
    };
    error?: string;
  }>> {
    return api.post<{
      success: boolean;
      elements?: {
        name?: string;
        places: string[];
        people: string[];
        emotions: string[];
        themes: string[];
        interests: string[];
        keywords: string[];
      };
      error?: string;
    }>('/generations/extract-story', { story });
  }
}

export const generationService = new GenerationService();
